import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from './api';
import PostCard from './PostCard';
import './Feed.css';

// Заготовка карточки: повторяет её очертания, поэтому при появлении
// настоящего поста лента не дёргается.
function PostSkeleton() {
  return (
    <div className="post-skeleton" aria-hidden="true">
      <div className="post-skeleton-head">
        <span className="skeleton-block skeleton-avatar"></span>
        <span className="skeleton-lines">
          <span className="skeleton-block skeleton-name"></span>
          <span className="skeleton-block skeleton-date"></span>
        </span>
      </div>
      <span className="skeleton-block skeleton-title"></span>
      <span className="skeleton-block skeleton-text"></span>
      <span className="skeleton-block skeleton-text short"></span>
    </div>
  );
}

// Размер порции: столько карточек рисуем сразу и столько добавляем за раз
const PAGE_SIZE = 12;

export default function Feed({ selectedSection, selectedSubcategory, searchQuery, searchFilter = 'all', user, refreshKey = 0, onCreatePost }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Сначала показываем первую порцию: отрисовка полутора сотен карточек
  // разом ощутимо задерживает открытие страницы. Остальные добавляются
  // по мере прокрутки.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // refreshKey меняется, когда пост создали, изменили или удалили в
  // админ-панели, — по нему лента перечитывает список без перезагрузки страницы.
  useEffect(() => {
    loadPosts();
    // При смене раздела или запроса список другой — начинаем с первой порции
    setVisibleCount(PAGE_SIZE);
  }, [selectedSection, selectedSubcategory, searchQuery, searchFilter, refreshKey]);

  const hasMore = visibleCount < posts.length;

  const showMore = useCallback(() => {
    setVisibleCount((current) => Math.min(current + PAGE_SIZE, posts.length));
  }, [posts.length]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !hasMore) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      // Очень старый браузер — показываем всё сразу, лишь бы не потерялось
      setVisibleCount(posts.length);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) showMore();
      },
      // Запас снизу: подгрузка начинается до того, как упрёмся в конец
      { rootMargin: '600px 0px' }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, showMore, posts.length]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPosts();
      
      let filteredPosts = data;
      
      // Фильтрация по разделу
      if (selectedSection) {
        filteredPosts = filteredPosts.filter(post => 
          post.section === selectedSection.id
        );
      }
      
      // Фильтрация по подкатегории
      if (selectedSubcategory) {
        filteredPosts = filteredPosts.filter(post => 
          post.subcategory === selectedSubcategory.id
        );
      }
      
      // Поиск с учетом фильтра
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        
        if (searchFilter === 'all' || searchFilter === 'posts') {
          // Поиск в постах
          filteredPosts = filteredPosts.filter(post => {
            const matchesTitle = post.title.toLowerCase().includes(query);
            const matchesContent = post.content && post.content.toLowerCase().includes(query);
            const matchesSection = post.section_title && post.section_title.toLowerCase().includes(query);
            const matchesSubcategory = post.subcategory_title && post.subcategory_title.toLowerCase().includes(query);
            const matchesFiles = post.attachments && post.attachments.some(att => {
              const fileName = (att.display_name || att.file_name || '').toLowerCase();
              return fileName.includes(query);
            });
            return matchesTitle || matchesContent || matchesSection || matchesSubcategory || matchesFiles;
          });
        } else if (searchFilter === 'files') {
          // Поиск только в файлах
          filteredPosts = filteredPosts.filter(post => {
            return post.attachments && post.attachments.some(att => {
              const fileName = (att.display_name || att.file_name || '').toLowerCase();
              return fileName.includes(query);
            });
          });
        } else if (searchFilter === 'sections') {
          // Поиск только в разделах
          filteredPosts = filteredPosts.filter(post => {
            return post.section_title && post.section_title.toLowerCase().includes(query);
          });
        } else if (searchFilter === 'subcategories') {
          // Поиск только в подкатегориях
          filteredPosts = filteredPosts.filter(post => {
            return post.subcategory_title && post.subcategory_title.toLowerCase().includes(query);
          });
        }
      }
      
      // Сортировка: сначала закрепленные, потом по дате
      filteredPosts.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setPosts(filteredPosts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Кнопка есть только у администраторов: App передаёт onCreatePost лишь им
  const createButton = onCreatePost && (
    <button type="button" className="feed-create-button" onClick={onCreatePost}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span>Создать пост</span>
    </button>
  );

  if (loading) {
    return (
      <div className="feed">
        <div className="feed-loading-note">
          <span className="loading-spinner"></span>
          <span>Загружаем публикации...</span>
        </div>
        <div className="posts-list">
          {[0, 1, 2].map((i) => <PostSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-error">
        <p>Ошибка: {error}</p>
        <button onClick={loadPosts} className="retry-button">Повторить</button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="feed-empty">
        <div className="empty-icon">📭</div>
        <h3>Нет постов</h3>
        <p>
          {searchQuery 
            ? 'По вашему запросу ничего не найдено'
            : 'В этом разделе пока нет публикаций'
          }
        </p>
        {createButton}
      </div>
    );
  }

  return (
    <div className="feed fade-in">
      <div className="feed-header">
        <h2 className="feed-title">
          {selectedSection 
            ? selectedSection.title 
            : selectedSubcategory
            ? selectedSubcategory.title
            : 'Все публикации'
          }
        </h2>
        <div className="feed-header-actions">
          <span className="feed-count">{posts.length} {posts.length === 1 ? 'публикация' : 'публикаций'}</span>
          {createButton}
        </div>
      </div>
      <div className="posts-list">
        {posts.slice(0, visibleCount).map((post, index) => (
          <PostCard 
            key={post.id} 
            post={post} 
            index={index}
            user={user}
            onPostUpdate={(updatedPost) => {
              if (updatedPost === null) {
                // Пост удален, убираем из списка
                setPosts(posts.filter(p => p.id !== post.id));
              } else {
                // Пост обновлен
                const updatedPosts = posts.map(p => p.id === updatedPost.id ? updatedPost : p);
                setPosts(updatedPosts);
              }
            }}
          />
        ))}
      </div>

      {hasMore && (
        // Блок и служит меткой для автоподгрузки, и остаётся кнопкой:
        // если наблюдатель за прокруткой почему-то не сработает,
        // публикации всё равно можно догрузить нажатием.
        <button
          type="button"
          className="feed-more"
          ref={sentinelRef}
          onClick={showMore}
          aria-label="Показать ещё публикации"
        >
          <span className="feed-loading-note">
            <span className="loading-spinner"></span>
            <span>Загружаем ещё публикации...</span>
          </span>
          <span className="feed-more-skeletons">
            <PostSkeleton />
            <PostSkeleton />
          </span>
        </button>
      )}
    </div>
  );
}

